import assign from 'lodash/assign';
import get from 'lodash/get';
import { initialize as initFn } from 'ldclient-js';
import uuid from 'uuid-v4';
import ApiConfig from 'admin/services/api/ApiConfig';
import SiteDesignerService from 'admin/services/api/SiteDesigner';
import { LaunchChangePlanServiceModule } from 'components/changePlan/launchChangePlan.service';
import { NavigationMenuComponentModule } from 'components/navigationMenu/navigationMenu.component';
import { StorefrontSliderModule } from 'components/storefrontSlider/storefrontSlider.module';
import { StoreInformationServiceModule } from 'services/data/storeInformation.service';
import { TrialServiceModule, EXPIRED, NOT_TRIAL, PARKED } from 'components/trial/trial.service';
import { TrialStatusBarModule } from 'components/trial/trialStatusBar';
import { VideoSliderModule } from 'components/videoSlider/videoSlider.component';
import { AuthServiceModule } from 'services/auth/auth.service';
import { Auth0AuthServiceModule } from 'services/auth/auth0auth.service';
import { AuthTokenServiceModule } from 'services/auth/authToken.service';
import { OnboardingServiceModule } from 'services/data/onboarding.service';
import { FounderGuideServiceModule } from 'services/utilities/founderGuide.service';
import { SubscriptionServiceModule } from 'services/access/subscription/subscription.service';
import { GreetingServiceModule } from 'services/data/greeting.service';
import { MyAccountServiceModule } from 'services/data/myAccount.service';
import { TermsAndConditionsModule } from 'admin/components/TermsAndConditionsModal/index.ng';
import { TermsAndConditionsServiceModule } from 'services/data/termsAndConditions.service';

import { AppServiceModule } from './app.service';

import './app.scss';

const SHOW_TERMS_TASK_ID = 'onboarding.termsAndConditions';

export const AppComponentModule = angular
    .module('app.appComponent', [
        TrialServiceModule,
        AuthServiceModule,
        AuthTokenServiceModule,
        Auth0AuthServiceModule,
        LaunchChangePlanServiceModule,
        TrialStatusBarModule,
        StorefrontSliderModule,
        StoreInformationServiceModule,
        AppServiceModule,
        NavigationMenuComponentModule,
        VideoSliderModule,
        OnboardingServiceModule,
        FounderGuideServiceModule,
        SubscriptionServiceModule,
        MyAccountServiceModule,
        TermsAndConditionsModule,
        GreetingServiceModule,
        TermsAndConditionsServiceModule
    ]).component('app', {
        template: '<ui-view></ui-view>'
    }).config(($stateProvider) => {
        $stateProvider.state({
            name: 'app',
            component: 'app',
            abstract: true
        }).state({
            name: 'auth',
            parent: 'app',
            abstract: true,
            template: `<navigation-menu
                            ng-if="app.shouldShowSideNavigationMenu()"
                            show-progress-bar="app.hasFounderGuideFeature"
                            progress-bar-value="app.founderGuideProgress"
                            trial-status="app.trialStatus" />
                       <ui-view id="main-app-view"></ui-view>
                       <terms-and-conditions-modal
                            open="app.showTermsAndConditionsModal"
                            on-accept="app.acceptTermsAndConditions"
                            container="app.termsAndConditionsContainer"
                            />
                       <trial-status-bar
                           ng-if="app.shouldShowTrialStatusBar() && !app.hasFounderGuideFeature"
                           trial-status="app.trialStatus">
                       </trial-status-bar>
                       <video-slider></video-slider>`,
            data: {
                requiresLogin: true
            },
            resolve: {
                cacheAccessToken: (authTokenService) => {
                    return authTokenService.getAccessToken();
                },
                initLaunchDarkly: (launchDarklyService, myAccountService) => {
                    launchDarklyService.unInit();
                    myAccountService.getUser()
                        .then(user => launchDarklyService.initFromToken({ createdOn: user.createdOn }));
                },
                hasFounderGuideFeature: (initLaunchDarkly, launchDarklyService) => {
                    return launchDarklyService.getFeature('founder-guide');
                },
                isNewAccount: (initLaunchDarkly, launchDarklyService, storeInformationService) => {
                    return storeInformationService.getStore()
                        .then(storeInformation => launchDarklyService.isNewAccount(storeInformation));
                },
                setTenantAndAuthToken: (authTokenService) => {
                    authTokenService.getHeaders().then((headers) => {
                        ApiConfig.setTenantId(headers.tenantId);
                        ApiConfig.setAuthToken(headers.token);
                        SiteDesignerService.setAuthToken(headers.token);
                    });
                },
                isOnHold: (subscriptionsService, $state) => {
                    return subscriptionsService.getSubscription().then((subscription) => {
                        if (subscription.features.isOnHold) {
                            return $state.go('onHold');
                        }
                    });
                }
            },
            controllerAs: 'app',
            controller: class AppController {
                constructor(
                    trialService,
                    $interval,
                    authService,
                    launchChangePlanService,
                    onboardingService,
                    founderGuideService,
                    subscriptionsService,
                    $rootScope,
                    appService,
                    $state,
                    $scope,
                    $http,
                    authTokenService,
                    auth0AuthService,
                    hasFounderGuideFeature,
                    isNewAccount,
                    termsAndConditionsService
                ) {
                    'ngInject';

                    assign(this, {
                        trialService,
                        $interval,
                        authService,
                        launchChangePlanService,
                        onboardingService,
                        founderGuideService,
                        subscriptionsService,
                        $rootScope,
                        appService,
                        $state,
                        $scope,
                        $http,
                        authTokenService,
                        auth0AuthService,
                        intervalDelay: 300000,
                        progressBarIntervalDelay: 1000,
                        hasFounderGuideFeature,
                        isNewAccount,
                        termsAndConditionsService,
                        showTermsAndConditionsModal: false,
                        NOT_TRIAL,
                        // Fixes bug with react modal being open at same time as angular modal
                        termsAndConditionsContainer: document.querySelector('app')
                    });
                }

                $onInit() {
                    this.appService.init();
                    this.getTrialStatus().then(() => {
                        if (this.trialService.isValidTrialPlanStatus(this.trialStatus)) {
                            this.startPollingTrialStatus();

                            if (this.hasFounderGuideFeature) {
                                this.setFounderGuideProgressValue();
                            }
                        }

                        if (this.trialStatus !== NOT_TRIAL) {
                            this.startPollingProgressBarStatus();
                            angular.element('body').addClass('trial-account');
                        }

                        this.authService.isImpersonator().then((isImpersonator) => {
                            if (!isImpersonator && this.trialStatus === NOT_TRIAL) {
                                this.handleTermsAndConditions();
                            }
                        });
                    });

                    this.acceptTermsAndConditions = this.acceptTermsAndConditions.bind(this);
                }

                $onDestroy() {
                    this.stopPollingTrialStatus();
                    this.stopPollingProgressBarStatus();
                    this.unbindStateChangeSuccess();
                }

                handleTermsAndConditions() {
                    return this.termsAndConditionsService.getTerms()
                        .then((termsResponse) => {
                            const latestTerms = get(termsResponse, 'items[0]');

                            if (latestTerms && latestTerms.termsId) {
                                this.latestTerms = latestTerms;
                                return this.termsAndConditionsService.getAcceptedTermsById(
                                    this.latestTerms.termsId
                                );
                            }
                        })
                        .catch((termsResponse) => {
                            if (termsResponse.status === 404) {
                                this.showTermsAndConditionsModal = true;
                            } else {
                                throw new Error(termsResponse);
                            }
                        });
                }

                logout() {
                    this.auth0AuthService.logout();
                }

                startPollingProgressBarStatus() {
                    this.getProgressBarInterval = this.$interval(
                        () => this.setFounderGuideProgressValue(),
                        this.progressBarIntervalDelay
                    );
                }

                stopPollingProgressBarStatus() {
                    this.$interval.cancel(this.getProgressBarInterval);
                }

                startPollingTrialStatus() {
                    this.getTrialStatusInterval = this.$interval(() => this.getTrialStatus(), this.intervalDelay);
                }

                stopPollingTrialStatus() {
                    this.$interval.cancel(this.getTrialStatusInterval);
                }

                getTrialStatus() {
                    return this.trialService.getTrialStatus().then((trialStatus) => {
                        this.trialStatus = trialStatus;
                        if (trialStatus === PARKED || trialStatus === EXPIRED) {
                            this.launchChangePlanService.openModal(null, null, true);
                            this.stopPollingTrialStatus();
                        }
                    }).catch(() => this.stopPollingTrialStatus());
                }

                setFounderGuideProgressValue() {
                    this.onboardingService.getState().then((state) => {
                        this.founderGuideService.updateStepsComplete(state);
                        let completedSteps = this.founderGuideService.getCompletedStepCount();
                        this.founderGuideProgress = (completedSteps / this.founderGuideService.steps.length) * 100;
                    });
                }

                shouldShowTrialStatusBar() {
                    return angular.isDefined(this.trialStatus) && this.trialStatus !== NOT_TRIAL;
                }

                shouldShowSideNavigationMenu() {
                    return angular.isDefined(this.$state.current.data.showSideNavigationMenu) ?
                        this.$state.current.data.showSideNavigationMenu :
                        true;
                }

                acceptTermsAndConditions() {
                    this.authTokenService.getEmail().then((email) => {
                        this.onboardingService.completeTask(SHOW_TERMS_TASK_ID);
                        this.termsAndConditionsService.acceptTerms(
                            this.latestTerms.termsId,
                            email
                        );
                        this.showTermsAndConditionsModal = false;
                        this.$scope.$apply();
                    });
                }
            }
        }).state({
            name: 'guest',
            parent: 'app',
            abstract: true,
            template: '<ui-view></ui-view>',
            resolve: {
                initLaunchDarkly: (launchDarklyService) => {
                    return launchDarklyService.init({
                        key: uuid(),
                        anonymous: true
                    }, initFn);
                }
            }
        });
    }).name;



// WEBPACK FOOTER //
// ./src/app/app.component.js